import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Check } from "lucide-react";
import api, { API } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("venmo");
  const [manualDone, setManualDone] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => toast.error("Order not found"));
  }, [orderId]);

  const payManual = async (m) => {
    try {
      await api.post("/payments/manual", { order_id: orderId, method: m });
      navigate(`/thank-you/${orderId}`);
    } catch { toast.error("Something went wrong"); }
  };

  if (!order) return <div className="max-w-3xl mx-auto px-5 py-20 font-display text-xl">Loading order…</div>;

  const tabs = [
    { id: "venmo", label: "Venmo", icon: Smartphone },
    { id: "cashapp", label: "Cash App", icon: Smartphone },
  ];

  return (
    <div className="max-w-4xl mx-auto px-5 py-12" data-testid="payment-page">
      <h1 className="font-display font-bold text-4xl sm:text-5xl">Checkout</h1>
      <p className="text-ink/60 text-lg mt-1 mb-8">Order #{order.id.slice(0, 8)}</p>

      <div className="grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3 space-y-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setMethod(t.id)} data-testid={`paytab-${t.id}`}
                className={`brut-btn ${method === t.id ? "bg-sun" : "bg-white"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {(method === "venmo" || method === "cashapp") && (
            <div className="brut-card p-6" data-testid={`pay-${method}`}>
              <h2 className="font-display font-bold text-2xl mb-1">{method === "venmo" ? "Pay with Venmo" : "Pay with Cash App"}</h2>
              <p className="text-ink/60 mb-4">Send <b>${order.total.toFixed(2)}</b> to the handle below, then tap the button so we know to look for it.</p>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {(method === "venmo" ? settings?.venmo_qr : settings?.cashapp_qr) ? (
                  <img src={`${API}/files/${(method === "venmo" ? settings.venmo_qr : settings.cashapp_qr)}`} alt="QR code"
                    className="w-40 h-40 object-contain brut border-ink bg-white p-2" data-testid="pay-qr" />
                ) : (
                  <div className="w-40 h-40 grid place-items-center brut border-ink bg-cream-solid text-center text-sm text-ink/50 p-3">QR code coming soon</div>
                )}
                <div>
                  <div className="text-sm text-ink/60">Send to</div>
                  <div className="font-display font-bold text-2xl" data-testid="pay-handle">
                    {method === "venmo" ? settings?.venmo_name : settings?.cashapp_name}
                  </div>
                </div>
              </div>
              {manualDone ? (
                <div className="mt-6 brut border-ink bg-sky/40 p-4 flex items-center gap-2 font-semibold" data-testid="manual-done">
                  <Check className="w-5 h-5" /> Got it! We'll confirm your payment and email your receipt.
                </div>
              ) : (
                <button onClick={() => payManual(method)} className="brut-btn bg-sun w-full mt-6" data-testid="pay-manual-btn">
                  I've sent the payment
                </button>
              )}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <div className="brut-card p-6 sticky top-24" data-testid="payment-summary">
            <h2 className="font-display font-bold text-xl mb-3">Order summary</h2>
            {order.items.map((i) => (
              <div key={i.lookup_key} className="flex justify-between py-1.5 text-sm border-b border-dashed border-ink/20">
                <span>{i.name} × {i.quantity}</span><span className="font-bold">${(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 mt-2"><span>Subtotal</span><span className="font-bold">${order.subtotal.toFixed(2)}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between py-1 text-tangerine font-bold" data-testid="summary-discount">
                <span>Discount ({order.discount_reason})</span><span>−${order.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-t-2 border-ink mt-2 text-lg">
              <span className="font-display font-bold">Total</span>
              <span className="font-display font-bold" data-testid="summary-total">${order.total.toFixed(2)}</span>
            </div>
            {order.occasion && <div className="tag-pill bg-sky mt-3">For: {order.occasion}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
